import React, { useCallback, useState } from "react";
import { Table, Button, Input, Alert, Tooltip } from "antd";
import NumberInput from "../common/NumberInput";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { DeleteOutlined } from "@ant-design/icons";
import { CarriagewayFootway, RoadType } from "../../types/tender.types";
import { COLORS } from "../../utils/constants";
import { useTableRenderDeferral } from "../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../common/TableLoadingPlaceholder";

interface CarriagewayFootwaySectionProps {
  carriagewayFootways: CarriagewayFootway[];
  roadTypes: RoadType[];
  onUpdate: (carriagewayFootways: CarriagewayFootway[]) => void;
  // Row selection is controlled by the parent so its "Delete Selected" button
  // (rendered next to the tab-level Configure button) can act on it.
  selectedRowKeys: React.Key[];
  onSelectedRowKeysChange: (keys: React.Key[]) => void;
  stickyOffset?: number;
  isViewMode?: boolean;
}

const CARRIAGEWAY_AREA_FIELDS: Array<keyof CarriagewayFootway> = [
  "tarmacSpec1", "tarmacSpec2", "tarmacSpec3", "tarmacSpec4",
  "blockPavedSpec1", "blockPavedSpec2", "blockPavedSpec3", "blockPavedSpec4",
];

// Renders column header title split into name + unit (matching CutFills pattern)
const ColTitle: React.FC<{ title: string }> = ({ title }) => {
  const unitMatch = title.match(/\s+(m[²³]?|Nr)$/);
  if (!unitMatch) {
    return (
      <Tooltip title={title}>
        <div className="cfw-header-title">{title}</div>
      </Tooltip>
    );
  }
  const unit = unitMatch[1];
  const name = title.replace(/\s+(m[²³]?|Nr)$/, "");
  return (
    <div className="text-center">
      <Tooltip title={name}>
        <div className="cfw-header-title">{name}</div>
      </Tooltip>
      <div className="cfw-header-unit">{unit}</div>
    </div>
  );
};

const CarriagewayFootwaySection: React.FC<CarriagewayFootwaySectionProps> = ({
  carriagewayFootways,
  roadTypes,
  onUpdate,
  selectedRowKeys,
  onSelectedRowKeysChange,
  stickyOffset = 310,
  isViewMode = false,
}) => {
  // Defers this table's first mount by one paint cycle so a tender with a
  // lot of roads doesn't make this tab look frozen while the table's
  // initial render/layout blocks the main thread - see
  // useTableRenderDeferral's own doc comment. Purely a perceived-lag fix:
  // once it flips false the table renders exactly as before.
  const isTableLoading = useTableRenderDeferral();

  const rtName = (id: string, fallback: string) =>
    roadTypes.find((r) => r.id === id)?.name || fallback;
  const deleteRecords = useCallback(
    (ids: React.Key[]) => {
      const idSet = new Set(ids);
      onUpdate(carriagewayFootways.filter((item) => !idSet.has(item.id)));
    },
    [carriagewayFootways, onUpdate],
  );

  // Delete confirmation modal — matches the DeleteConfirmationModal pattern used
  // by Groundworks tables, rather than the antd Popconfirm previously used here.
  // idsToDelete is used for single-row delete; bulk delete is triggered by the
  // parent (its "Delete Selected" button lives next to the tab's Configure button).
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [idsToDelete, setIdsToDelete] = useState<React.Key[]>([]);
  const confirmDelete = () => {
    deleteRecords(idsToDelete);
    setDeleteModalVisible(false);
    setIdsToDelete([]);
    onSelectedRowKeysChange([]);
  };
  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setIdsToDelete([]);
  };

  const updateField = useCallback(
    (id: string, field: keyof CarriagewayFootway, value: string | number) => {
      const updated = carriagewayFootways.map((item) => {
        if (item.id !== id) return item;
        const next = { ...item, [field]: value };

        if (CARRIAGEWAY_AREA_FIELDS.includes(field)) {
          next.totalRoadArea =
            (next.tarmacSpec1 || 0) + (next.tarmacSpec2 || 0) +
            (next.tarmacSpec3 || 0) + (next.tarmacSpec4 || 0) +
            (next.blockPavedSpec1 || 0) + (next.blockPavedSpec2 || 0) +
            (next.blockPavedSpec3 || 0) + (next.blockPavedSpec4 || 0);
        }

        return next;
      });
      onUpdate(updated);
    },
    [carriagewayFootways, onUpdate],
  );

  const totals = carriagewayFootways.reduce(
    (acc, item) => {
      (Object.keys(acc) as Array<keyof typeof acc>).forEach((k) => {
        acc[k] = acc[k] + ((item[k] as number) || 0);
      });
      return acc;
    },
    {
      tarmacSpec1: 0, tarmacSpec2: 0, tarmacSpec3: 0, tarmacSpec4: 0,
      blockPavedSpec1: 0, blockPavedSpec2: 0, blockPavedSpec3: 0, blockPavedSpec4: 0,
      totalRoadArea: 0,
    },
  );

  const inputCol = (
    title: string,
    dataIndex: keyof CarriagewayFootway,
    width: number,
    precision = 3,
  ) => ({
    title: <ColTitle title={title} />,
    dataIndex,
    key: dataIndex as string,
    width,
    align: "right" as const,
    render: (value: number, record: CarriagewayFootway) => (
      <NumberInput
        value={value ?? 0}
        onChange={(val) => updateField(record.id, dataIndex, val)}
        decimalPlaces={precision}
        isInteger={precision === 0}
        style={{ width: "100%", textAlign: "right" }}
        size="small"
      />
    ),
  });

  const calcCol = (
    title: string,
    dataIndex: keyof CarriagewayFootway,
    width: number,
  ) => ({
    title: <ColTitle title={title} />,
    dataIndex,
    key: dataIndex as string,
    width,
    align: "right" as const,
    render: (value: number) => (
      <span style={{ fontWeight: 600, color: COLORS.primary }}>
        {(value || 0).toFixed(3)}
      </span>
    ),
  });

  const columns = [
    {
      title: "Road Name",
      dataIndex: "roadName",
      key: "roadName",
      width: 120,
      fixed: "left" as const,
      render: (value: string, record: CarriagewayFootway) => (
        <Input
          value={value}
          onChange={(e) => updateField(record.id, "roadName", e.target.value)}
          placeholder="Road name"
          size="small"
        />
      ),
    },
    inputCol(`${rtName('tarmac-1', 'Tarmac Spec 1')} m²`, "tarmacSpec1", 115),
    inputCol(`${rtName('tarmac-2', 'Tarmac Spec 2')} m²`, "tarmacSpec2", 115),
    inputCol(`${rtName('tarmac-3', 'Tarmac Spec 3')} m²`, "tarmacSpec3", 115),
    inputCol(`${rtName('tarmac-4', 'Tarmac Spec 4')} m²`, "tarmacSpec4", 115),
    inputCol(`${rtName('block-1', 'Block Paved Spec 1')} m²`, "blockPavedSpec1", 140),
    inputCol(`${rtName('block-2', 'Block Paved Spec 2')} m²`, "blockPavedSpec2", 140),
    inputCol(`${rtName('block-3', 'Block Paved Spec 3')} m²`, "blockPavedSpec3", 140),
    inputCol(`${rtName('block-4', 'Block Paved Spec 4')} m²`, "blockPavedSpec4", 140),
    calcCol("Total Road Area m²", "totalRoadArea", 125),
    {
      title: "Actions",
      key: "actions",
      width: 75,
      fixed: "right" as const,
      align: 'center' as const,
      render: (_: unknown, record: CarriagewayFootway) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          size="small"
          onClick={() => {
            setIdsToDelete([record.id]);
            setDeleteModalVisible(true);
          }}
        />
      ),
    },
  ];

  // Ordered leaf columns for summary row indexing (matches column order above)
  type TotalsKey = keyof typeof totals;
  const summaryLeaves: Array<TotalsKey | "roadName" | "actions"> = [
    "roadName",
    "tarmacSpec1", "tarmacSpec2", "tarmacSpec3", "tarmacSpec4",
    "blockPavedSpec1", "blockPavedSpec2", "blockPavedSpec3", "blockPavedSpec4",
    "totalRoadArea",
    "actions",
  ];

  const isCalcField = (k: string) => k === "totalRoadArea";

  return (
    <>
      {isTableLoading ? (
        <TableLoadingPlaceholder />
      ) : (
      <Table
        className="carriageway-footway-table"
        columns={columns}
        dataSource={carriagewayFootways}
        rowKey="id"
        rowSelection={isViewMode ? undefined : {
          selectedRowKeys,
          onChange: onSelectedRowKeysChange,
        }}
        pagination={false}
        scroll={{ x: 1400 }}
        sticky={{ offsetHeader: stickyOffset, offsetScroll: 9999 }}
        size="small"
        bordered
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}>
              {/* colSpan merges the row-selection checkbox column (when present) with Road Name,
                  since rowSelection inserts a checkbox column before all defined columns. */}
              <Table.Summary.Cell key="roadName" index={0} colSpan={isViewMode ? 1 : 2} align="center">
                <span style={{ color: "#000" }}>Totals</span>
              </Table.Summary.Cell>
              {summaryLeaves.filter((key) => key !== "roadName").map((key, idx) => {
                // Accounts for the checkbox column (when present) and the merged Totals cell above.
                const colIndex = idx + (isViewMode ? 1 : 2);
                if (key === "actions") {
                  return <Table.Summary.Cell key="actions" index={colIndex} />;
                }
                const val = (totals[key as TotalsKey] as number) || 0;
                return (
                  <Table.Summary.Cell key={key} index={colIndex} align="right">
                    <span
                      style={{
                        color: isCalcField(key) ? COLORS.primary : "#000",
                        paddingRight: summaryLeaves.length-1 ==colIndex?0:7.5,
                      }}
                    >
                      {val.toFixed(3)}
                    </span>
                  </Table.Summary.Cell>
                );
              })}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
      )}

      {carriagewayFootways.length === 0 && (
        <Alert
          message="No carriageway records. Click 'Add Road' to get started."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      <style>{`
        .carriageway-footway-table {
          font-size: 14px;
        }

        .carriageway-footway-table .ant-table {
          border: none !important;
        }

        .carriageway-footway-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-radius: 0 !important;
        }

        .carriageway-footway-table .ant-table-thead > tr > th {
          min-width: 100px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          padding: 10px 6px 8px 6px !important;
          text-align: center !important;
          font-size: 12px !important;
          vertical-align: top !important;
        }

        .carriageway-footway-table .ant-table-thead > tr > th .cfw-header-title {
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          display: block !important;
          width: 100% !important;
        }

        .carriageway-footway-table .ant-table-thead > tr > th .cfw-header-unit {
          font-size: 11px !important;
          color: #6b7280 !important;
          font-weight: 500 !important;
        }

        .carriageway-footway-table .ant-table-thead > tr > th.ant-table-cell-fix-left,
        .carriageway-footway-table .ant-table-thead > tr > th.ant-table-cell-fix-right {
          background-color: ${COLORS.tableHeaderBg} !important;
          z-index: 100 !important;
        }

        .carriageway-footway-table .ant-table-tbody > tr > td.ant-table-cell-fix-left {
          background-color: #fff !important;
        }

        .carriageway-footway-table .ant-table-tbody > tr:hover > td.ant-table-cell-fix-left {
          background-color: #fafafa !important;
        }

        /* Bulk-select checkboxes should not highlight the row */
        .carriageway-footway-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }
        .carriageway-footway-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
          background-color: #fafafa !important;
        }

        /* The merged Totals cell (checkbox + Road Name) is fixed-left, so the
           tbody fix-left override above would otherwise paint it white instead
           of matching the rest of the totals row. */
        .carriageway-footway-table .ant-table-summary td.ant-table-cell-fix-left {
          background-color: #FFF7E6 !important;
        }

        .ant-table-thead > tr > th[data-carriageway-footway] {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
        }
      `}</style>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={idsToDelete.length > 1 ? `Are you sure you want to delete ${idsToDelete.length} records?` : undefined}
      />
    </>
  );
};

export default CarriagewayFootwaySection;
